import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getUserRole, clearAccessToken, clearRefreshToken } from 'utils/auth/auth';
import { profileService } from 'services/auth/authService';
import { useTranslation } from 'react-i18next';
import { RootState } from 'config/redux/store';
import { useTheme } from 'contexts/ThemeContext';
import avatar from 'assets/images/avatars/avatar.svg';
import {
  LayoutDashboard,
  Instagram,
  Zap,
  Send,
  MessageCircleMore,
  Heart,
  UserRoundPlus,
  StickyNote,
  SquarePlay,
  Gem,
  CreditCard,
  CircleHelp,
  EllipsisVertical,
  User,
  LogOut,
} from 'lucide-react';

interface SidebarProps {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  setIsLoggedIn: (isLoggedIn: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ sidebarOpen, toggleSidebar, setIsLoggedIn }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  const [userName, setUserName] = useState<string>('');
  const [profileDropdownOpen, setProfileDropdownOpen] = useState<boolean>(false);
  const profileDropdownRef = useRef<HTMLDivElement>(null);

  const userRole = getUserRole();
  const normalizedRole = userRole?.toLowerCase().replace(/\s+/g, '') || '';
  const isAdmin = normalizedRole === 'admin' || normalizedRole === 'superadmin';
  const isSuperAdmin = normalizedRole === 'superadmin';

  const { planName, subscriptionCategories } = useSelector((state: RootState) => state.user);

  const hasCategory = (category: string) =>
    subscriptionCategories.length === 0 || subscriptionCategories.includes(category);

  // Fetch user profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await profileService.getProfile();
        if (response.data) {
          if ('profile_picture' in response.data && typeof response.data.profile_picture === 'string') {
            setProfilePicture(response.data.profile_picture);
          }
          if ('first_name' in response.data && 'last_name' in response.data) {
            const name = [response.data.first_name, response.data.last_name].filter(Boolean).join(' ');
            setUserName(name || 'User');
          }
        }
      } catch {
        // Profile fetch failed silently
      }
    };
    fetchProfile();
  }, []);

  // Close profile dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
        setProfileDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleLogout = () => {
    clearAccessToken();
    clearRefreshToken();
    setIsLoggedIn(false);
    navigate('/login');
  };

  const handleLinkClick = () => {
    if (sidebarOpen) {
      toggleSidebar();
    }
  };

  // Style helpers
  const sectionLabelClass = `px-1.5 py-1.5 text-xs font-bold leading-4 ${
    isDark ? 'text-fc-dark-text-secondary' : 'text-fc-text-secondary'
  }`;

  const getItemClass = (isActive: boolean) =>
    `flex items-center gap-2 px-1.5 py-1.5 rounded-md text-sm font-medium leading-5 transition-all ${
      isActive
        ? isDark
          ? 'bg-fc-sidebar-active-dark text-white'
          : 'bg-fc-sidebar-active-light text-fc-sidebar-active-text-light'
        : isDark
        ? 'text-fc-dark-text-primary hover:bg-fc-sidebar-active-dark/50'
        : 'text-fc-text-primary hover:bg-fc-sidebar-active-light'
    }`;

  const iconSize = 16;

  return (
    <>
      {/* Mobile Backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={toggleSidebar}
        />
      )}

      <aside
        className={`w-[200px] fixed left-0 top-0 bottom-0 flex flex-col justify-between pt-3 pb-3 pl-3 pr-0 gap-3 font-geist transition-transform duration-300 z-50 ${
          isDark ? 'bg-fc-sidebar-dark' : 'bg-fc-sidebar-light'
        } ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
      >
        {/* Top - Navigation */}
        <nav className={`flex-1 overflow-y-auto flex flex-col gap-3 pr-3 ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`}>
          {/* Overview */}
          <div className="flex flex-col gap-0.5">
            <span className={sectionLabelClass}>{t('navigation.overview')}</span>
            <Link
              to="/dashboard"
              onClick={handleLinkClick}
              className={getItemClass(location.pathname === '/dashboard')}
            >
              <LayoutDashboard size={iconSize} />
              <span>{t('navigation.dashboard')}</span>
            </Link>
          </div>

          {/* Workspace */}
          <div className="flex flex-col gap-0.5">
            <span className={sectionLabelClass}>{t('navigation.workspace')}</span>
            <Link
              to="/accounts"
              onClick={handleLinkClick}
              className={getItemClass(location.pathname.includes('/accounts'))}
            >
              <Instagram size={iconSize} />
              <span>{t('navigation.accounts')}</span>
            </Link>
            <Link
              to="/rules"
              onClick={handleLinkClick}
              className={getItemClass(location.pathname === '/rules')}
            >
              <Zap size={iconSize} />
              <span>{t('navigation.rules')}</span>
            </Link>
          </div>

          {/* Activity */}
          {(hasCategory('Messages') || hasCategory('Comments') || hasCategory('Likes') || hasCategory('Following') || hasCategory('Posts') || hasCategory('Story')) && (
          <div className="flex flex-col gap-0.5">
            <span className={sectionLabelClass}>{t('navigation.activity')}</span>
            {hasCategory('Messages') && (
            <Link
              to="/actions/messages"
              onClick={handleLinkClick}
              className={getItemClass(location.pathname.includes('/actions/messages'))}
            >
              <Send size={iconSize} />
              <span>{t('navigation.messages')}</span>
            </Link>
            )}
            {hasCategory('Comments') && (
            <Link
              to="/actions/comments"
              onClick={handleLinkClick}
              className={getItemClass(location.pathname.includes('/actions/comments'))}
            >
              <MessageCircleMore size={iconSize} />
              <span>{t('navigation.comments')}</span>
            </Link>
            )}
            {hasCategory('Likes') && (
            <Link
              to="/actions/likes"
              onClick={handleLinkClick}
              className={getItemClass(location.pathname.includes('/actions/likes'))}
            >
              <Heart size={iconSize} />
              <span>{t('navigation.likes')}</span>
            </Link>
            )}
            {hasCategory('Following') && (
            <Link
              to="/actions/followings"
              onClick={handleLinkClick}
              className={getItemClass(location.pathname.includes('/actions/followings'))}
            >
              <UserRoundPlus size={iconSize} />
              <span>{t('navigation.followings')}</span>
            </Link>
            )}
            {hasCategory('Posts') && (
            <Link
              to="/actions/posts"
              onClick={handleLinkClick}
              className={getItemClass(location.pathname.includes('/actions/posts'))}
            >
              <StickyNote size={iconSize} />
              <span>{t('navigation.posts')}</span>
            </Link>
            )}
            {hasCategory('Story') && (
            <Link
              to="/actions/story"
              onClick={handleLinkClick}
              className={getItemClass(location.pathname.includes('/actions/story'))}
            >
              <SquarePlay size={iconSize} />
              <span>{t('navigation.story')}</span>
            </Link>
            )}
          </div>
          )}

          {/* Plan */}
          <div className="flex flex-col gap-0.5">
            <span className={sectionLabelClass}>{t('navigation.planSection')}</span>
            <Link
              to="/subscriptions"
              onClick={handleLinkClick}
              className={getItemClass(location.pathname === '/subscriptions')}
            >
              <Gem size={iconSize} />
              <span>{t('navigation.subscriptions')}</span>
            </Link>
            <Link
              to="/billing"
              onClick={handleLinkClick}
              className={getItemClass(location.pathname === '/billing')}
            >
              <CreditCard size={iconSize} />
              <span>{t('navigation.billing')}</span>
            </Link>
          </div>

          {/* Support */}
          <div className="flex flex-col gap-0.5">
            <span className={sectionLabelClass}>{t('navigation.supportSection')}</span>
            <Link
              to="/support"
              onClick={handleLinkClick}
              className={getItemClass(location.pathname.includes('/support'))}
            >
              <CircleHelp size={iconSize} />
              <span>{t('navigation.helpAndSupport')}</span>
            </Link>
          </div>

          {/* Admin (role-based) */}
          {isAdmin && (
            <div className="flex flex-col gap-0.5">
              <span className={sectionLabelClass}>{t('navigation.admin')}</span>
              <Link
                to="/admin"
                onClick={handleLinkClick}
                className={getItemClass(location.pathname === '/admin')}
              >
                <LayoutDashboard size={iconSize} />
                <span>{t('navigation.adminDashboard')}</span>
              </Link>
              <Link
                to="/admin/subscriptions"
                onClick={handleLinkClick}
                className={getItemClass(location.pathname === '/admin/subscriptions')}
              >
                <Gem size={iconSize} />
                <span>{t('navigation.adminSubscriptions')}</span>
              </Link>
              <Link
                to="/admin/accounts-analysis"
                onClick={handleLinkClick}
                className={getItemClass(location.pathname === '/admin/accounts-analysis')}
              >
                <Instagram size={iconSize} />
                <span>{t('navigation.adminAccountsAnalysis')}</span>
              </Link>
            </div>
          )}

          {/* Super Admin (role-based) */}
          {isSuperAdmin && (
            <div className="flex flex-col gap-0.5">
              <span className={sectionLabelClass}>{t('navigation.superAdmin')}</span>
              <Link
                to="/super-admin/users"
                onClick={handleLinkClick}
                className={getItemClass(location.pathname === '/super-admin/users')}
              >
                <UserRoundPlus size={iconSize} />
                <span>{t('navigation.superAdminUsers')}</span>
              </Link>
              <Link
                to="/super-admin/accounts"
                onClick={handleLinkClick}
                className={getItemClass(location.pathname === '/super-admin/accounts')}
              >
                <Instagram size={iconSize} />
                <span>{t('navigation.superAdminAccounts')}</span>
              </Link>
              <Link
                to="/super-admin/jobs"
                onClick={handleLinkClick}
                className={getItemClass(location.pathname === '/super-admin/jobs')}
              >
                <Zap size={iconSize} />
                <span>{t('navigation.superAdminJobs')}</span>
              </Link>
              <Link
                to="/super-admin/queue"
                onClick={handleLinkClick}
                className={getItemClass(location.pathname === '/super-admin/queue')}
              >
                <LayoutDashboard size={iconSize} />
                <span>{t('navigation.superAdminQueue')}</span>
              </Link>
              <Link
                to="/super-admin/anbox"
                onClick={handleLinkClick}
                className={getItemClass(location.pathname === '/super-admin/anbox')}
              >
                <LayoutDashboard size={iconSize} />
                <span>{t('navigation.superAdminAnbox')}</span>
              </Link>
              <Link
                to="/super-admin/proxies"
                onClick={handleLinkClick}
                className={getItemClass(location.pathname === '/super-admin/proxies')}
              >
                <LayoutDashboard size={iconSize} />
                <span>{t('navigation.superAdminProxies')}</span>
              </Link>
            </div>
          )}
        </nav>

        {/* Bottom - Profile + Upgrade */}
        <div className="flex flex-col gap-2 pr-3">
          {/* Profile Card */}
          <div className="relative" ref={profileDropdownRef}>
            <div
              className={`flex items-center gap-1 p-2 rounded-fc cursor-pointer border ${
                isDark ? 'border-fc-sidebar-active-dark' : 'border-fc-border'
              }`}
              onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
            >
              <img
                src={profilePicture || avatar}
                alt="Profile"
                className={`w-8 h-8 rounded-full object-cover shrink-0 border ${
                  isDark ? 'border-fc-sidebar-active-dark' : 'border-fc-border'
                }`}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  e.currentTarget.src = avatar;
                }}
              />
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-medium leading-4 truncate ${
                  isDark ? 'text-fc-dark-text-primary' : 'text-fc-text-primary'
                }`}>
                  {userName || 'User'}
                </p>
                <p className={`text-xs font-normal leading-4 truncate ${
                  isDark ? 'text-fc-dark-text-secondary' : 'text-fc-text-secondary'
                }`}>
                  {planName || 'Free plan'}
                </p>
              </div>
              <EllipsisVertical
                size={16}
                className={`shrink-0 ${isDark ? 'text-fc-dark-text-primary' : 'text-fc-text-primary'}`}
              />
            </div>

            {/* Profile Dropdown */}
            {profileDropdownOpen && (
              <div
                className={`absolute bottom-full left-0 right-0 mb-1 rounded-fc shadow-lg border py-1 z-50 ${
                  isDark
                    ? 'bg-fc-sidebar-dark border-fc-sidebar-active-dark'
                    : 'bg-white border-fc-border'
                }`}
              >
                <Link
                  to="/settings"
                  onClick={() => {
                    setProfileDropdownOpen(false);
                    handleLinkClick();
                  }}
                  className={`flex items-center gap-2 px-3 py-2 text-xs font-medium transition-colors ${
                    isDark
                      ? 'text-fc-dark-text-primary hover:bg-fc-sidebar-active-dark'
                      : 'text-fc-text-primary hover:bg-fc-sidebar-active-light'
                  }`}
                >
                  <User size={14} />
                  <span>{t('navigation.profile')}</span>
                </Link>
                <div className={`my-1 h-px ${isDark ? 'bg-fc-sidebar-active-dark' : 'bg-fc-border'}`} />
                <button
                  onClick={handleLogout}
                  className={`flex items-center gap-2 w-full px-3 py-2 text-xs font-medium transition-colors text-red-500 ${
                    isDark ? 'hover:bg-fc-sidebar-active-dark' : 'hover:bg-red-50'
                  }`}
                >
                  <LogOut size={14} />
                  <span>{t('navigation.logout')}</span>
                </button>
              </div>
            )}
          </div>

          {/* Upgrade Plan Button — hidden for Elite subscribers */}
          {planName?.toLowerCase() !== 'elite' && (
            <button
              onClick={() => navigate('/subscriptions')}
              className={`flex items-center justify-center gap-1 w-full px-3 py-2 rounded-fc text-sm font-medium leading-5 transition-all ${
                isDark
                  ? 'bg-fc-dark-text-primary text-fc-sidebar-active-text-light shadow-[0_0_10px_#9810FA]'
                  : 'bg-fc-text-primary text-white shadow-[0_0_10px_#DAB2FF]'
              }`}
            >
              <Gem size={20} />
              <span>{t('navigation.upgradePlan')}</span>
            </button>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
